import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Button, 
  Typography, 
  Modal, 
  Alert, 
  TextField,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  LinearProgress
} from '@mui/material';
import { 
  Close, 
  ReportProblem,
  Send,
  PhotoCamera
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import PhotoCaptureSection from './PhotoCaptureSection';

const StyledModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
}));
const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 600,
  maxHeight: '90vh',
  overflow: 'auto',
  position: 'relative', // Important pour l'overlay
}));

const MIN_OVERLAY_DISPLAY_TIME = 1500; // en millisecondes - augmenté pour garantir la visibilité

const ProblemReportModal = ({
  open,
  onClose,
  reservation,
  onSubmit,
  loading = false,
  submitError: externalSubmitError = ''
}) => {
  const { t } = useTranslation('problemReport');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState([]);
  const [validationError, setValidationError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const photoCaptureRef = useRef(null);
  const submitErrorRef = useRef('');
  // Lock pour empêcher le clear prématuré de l'erreur
  const isErrorLocked = useRef(false);

  // Utiliser l'erreur externe ou l'erreur interne
  const displayError = externalSubmitError || submitError;

  // Log les changements de submitError
  useEffect(() => {
    submitErrorRef.current = displayError;
  }, [displayError]);

  // Effet pour auto-clear l'erreur de soumission après 8 secondes
  useEffect(() => {
    if (displayError) {
      isErrorLocked.current = true;
      const timer = setTimeout(() => {
        isErrorLocked.current = false;
        // Seulement clear l'erreur interne, pas l'erreur externe
        if (!externalSubmitError) {
          setSubmitError('');
        }
      }, 8000); // 8 secondes

      return () => {
        clearTimeout(timer);
      };
    } else {
      isErrorLocked.current = false;
    }
  }, [displayError, externalSubmitError]);

  // Reset du modal
  const resetModal = useCallback(() => {
    setDescription('');
    setPhotos([]);
    setValidationError('');
    // Ne jamais clear submitError ici, il sera clear uniquement par le timeout
    setSubmitting(false);
    // Fermer la caméra au reset du modal
    if (photoCaptureRef.current && photoCaptureRef.current.isShowingCamera()) {
      photoCaptureRef.current.stopCamera();
    }
  }, []);

  // Fermer modal avec reset
  const handleClose = useCallback(() => {
    if (loading || submitting) return;
    setDescription('');
    setPhotos([]);
    setValidationError('');
    // Ne jamais clear submitError ici, il sera clear uniquement par le timeout
    setSubmitting(false);
    if (photoCaptureRef.current && photoCaptureRef.current.isShowingCamera()) {
      photoCaptureRef.current.stopCamera();
    }
    onClose();
  }, [loading, submitting, onClose]);

  // Soumission du rapport
  const handleSubmit = useCallback(async () => {
    if (!description.trim()) {
      setValidationError(t('validation.descriptionRequired'));
      return;
    }

    if (description.trim().length < 10) {
      setValidationError(t('validation.descriptionTooShort'));
      return;
    }

    if (description.trim().length > 2000) {
      setValidationError(t('validation.descriptionTooLong'));
      return;
    }

    const startTime = Date.now();
    
    // Activer l'overlay de chargement immédiatement
    setSubmitting(true);
    // S'assurer que le changement d'état est appliqué avant de continuer
    await new Promise(resolve => setTimeout(resolve, 0));
    
    setValidationError('');

    try {
      // Convertir les photos en base64
      const convertedPhotos = await Promise.all(
        photos.map(photo => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                id: photo.id,
                data: e.target.result, // Base64 data URL
                filename: photo.file.name,
                mimetype: photo.file.type,
                size: photo.file.size,
                photo_type: photo.photo_type,
                description: photo.description || '',
                order_index: photo.order_index
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(photo.file);
          });
        })
      );

      const reportData = {
        reservation_id: reservation?.id,
        description: description.trim(),
        photos: convertedPhotos,
        type: 'problem_report',
        timestamp: new Date().toISOString()
      };

      await onSubmit(reportData);

      // Garantir un affichage minimum de l'overlay
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, MIN_OVERLAY_DISPLAY_TIME - elapsed);
      if (remaining > 0) {
        console.log(`⌛ [ProblemReportModal] Attente supplémentaire de ${remaining}ms pour l'overlay`);
        await new Promise(resolve => setTimeout(resolve, remaining));
      }

      console.log('✅ [ProblemReportModal] Rapport envoyé avec succès, fermeture du modal');
      
      // Succès - fermer le modal
      setDescription('');
      setPhotos([]);
      setValidationError('');
      if (photoCaptureRef.current && photoCaptureRef.current.isShowingCamera()) {
        photoCaptureRef.current.stopCamera();
      }
      
      // Maintenant on peut stopper l'overlay et fermer
      setSubmitting(false);
      onClose();
      
    } catch (error) {
      console.error('❌ [ProblemReportModal] Erreur lors de l\'envoi:', error);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, MIN_OVERLAY_DISPLAY_TIME - elapsed);
      if (remaining > 0) {
        await new Promise(resolve => setTimeout(resolve, remaining));
      }
      setSubmitting(false);
      // L'erreur est gérée par le parent, ne pas fermer la modale ici
    }
  }, [description, photos, reservation, onSubmit, onClose, t]);

  if (!reservation) return null;

  return (
    <StyledModal
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <StyledCard>
        {/* Overlay de progression pendant l'envoi */}
        {(submitting || loading) && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              borderRadius: 1, // Même border-radius que la card
              animation: 'fadeIn 0.2s ease-in-out',
              '@keyframes fadeIn': {
                '0%': { opacity: 0 },
                '100%': { opacity: 1 }
              }
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                padding: 4,
                boxShadow: 3,
                textAlign: 'center',
                minWidth: 300
              }}
            >
              <CircularProgress size={60} thickness={4} color="primary" />
              <Box mt={3}>
                <Typography variant="h6" gutterBottom color="primary" fontWeight={600}>
                  {t('progress.sending', 'Envoi en cours...')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('progress.pleaseWait', 'Veuillez patienter, votre rapport est en cours d\'envoi')}
                </Typography>
                {photos.length > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('progress.uploadingPhotos', 'Téléchargement de {{count}} photo(s)...', { count: photos.length })}
                  </Typography>
                )}
                <LinearProgress 
                  sx={{ 
                    width: '100%',
                    height: 6,
                    borderRadius: 3,
                    mt: 2
                  }} 
                />
              </Box>
            </Box>
          </Box>
        )}
        
        <CardContent>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <ReportProblem color="error" />
              <Typography variant="h6" component="h2">
                {t('title')}
              </Typography>
            </Box>
            <Button
              onClick={handleClose}
              disabled={loading || submitting}
              size="small"
              sx={{ minWidth: 'auto', p: 1 }}
            >
              <Close />
            </Button>
          </Box>

          {/* Informations réservation */}
          <Box mb={3} p={2} bgcolor="grey.50" borderRadius={1}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('reservation.info')}
            </Typography>
            <Typography variant="body2">
              <strong>{t('reservation.id')}:</strong> #{reservation.id}
            </Typography>
            <Typography variant="body2">
              <strong>{t('reservation.equipment')}:</strong> {reservation.listing?.title || t('reservation.unknown')}
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Section photos */}
          <Box mb={3}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhotoCamera />
              {t('photos.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('photos.description')}
            </Typography>
            
            <PhotoCaptureSection
              ref={photoCaptureRef}
              photos={photos}
              onPhotosChange={setPhotos}
              maxPhotos={5}
              sectionTitle=""
              showTitle={false}
            />
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Description du problème */}
          <Box mb={3}>
            <Typography variant="subtitle1" gutterBottom>
              {t('description.title')} *
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('description.placeholder')}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('description.placeholder')}
              disabled={loading || submitting}
              error={Boolean(validationError)}
              helperText={
                validationError || 
                t('validation.characterCount', { count: description.length })
              }
              inputProps={{
                maxLength: 2000
              }}
            />
          </Box>

          {/* Erreurs de soumission */}
          {displayError && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                fontSize: '1rem',
                fontWeight: 'bold',
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              {displayError}
            </Alert>
          )}

          {/* Boutons d'action */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
            <Box display="flex" gap={1}>
              <Button 
                onClick={handleClose} 
                disabled={loading || submitting}
                variant="outlined"
              >
                {t('actions.cancel')}
              </Button>
            </Box>
            
            <Button
              variant="contained"
              startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
              onClick={handleSubmit}
              disabled={loading || submitting || !description.trim()}
              color="error"
            >
              {submitting ? t('actions.submitting') : t('actions.submit')}
            </Button>
          </Box>
        </CardContent>
      </StyledCard>
    </StyledModal>
  );
}

export default ProblemReportModal;
